import React, { useState } from "react";

/**
 * Página pública de solicitud de eliminación de cuenta.
 * Requerida por Google Play (Data safety → Data deletion).
 * Ruta: /delete-account  ·  No requiere login.
 */
export default function DeleteAccountView() {
  const APP_NAME = "Turismo Madre de Dios";
  const CONTACT_EMAIL = "hugo@turismomdd.pe";
  const COMPANY = "Turismo Madre de Dios S.A.C.";

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");

  const mailtoHref = () => {
    const subject = encodeURIComponent(`Solicitud de eliminación de cuenta — ${APP_NAME}`);
    const body = encodeURIComponent(
      `Solicito la eliminación permanente de mi cuenta y datos asociados.\n\n` +
      `Correo registrado: ${email || "(indicar)"}\n` +
      `Teléfono registrado: ${phone || "(indicar)"}\n` +
      `Motivo (opcional): ${reason || "-"}\n\n` +
      `Entiendo que la eliminación es irreversible y se procesará en un plazo de hasta 30 días.`
    );
    return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-white text-neutral-900 py-12 px-6" data-testid="delete-account-view">
      <div className="max-w-3xl mx-auto">
        <div className="border-b-4 border-[#C6E90C] pb-6 mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#3949AB]">Eliminación de cuenta</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mt-2">{APP_NAME}</h1>
          <p className="text-sm text-neutral-600 mt-3">
            Solicita la eliminación permanente de tu cuenta y datos asociados.
          </p>
        </div>

        <section className="mb-8 leading-relaxed">
          <h2 className="font-display font-bold text-2xl border-l-4 border-[#C6E90C] pl-3 mb-3">Qué se elimina</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Perfil (nombre, correo, teléfono, foto).</li>
            <li>Historial de viajes personales (origen, destino, tarifa, calificaciones).</li>
            <li>Direcciones favoritas y destinos guardados.</li>
            <li>Tokens de notificaciones push (OneSignal / FCM) y sesiones activas.</li>
            <li>Preferencias, credenciales de acceso y datos biométricos locales.</li>
          </ul>
        </section>

        <section className="mb-8 leading-relaxed">
          <h2 className="font-display font-bold text-2xl border-l-4 border-[#C6E90C] pl-3 mb-3">Qué se conserva</h2>
          <p>
            Por obligaciones fiscales y de auditoría (Ley 29733 del Perú), retendremos <b>hasta 5 años</b> los
            registros anonimizados de comprobantes de pago y viajes ya completados. Estos registros no permiten
            identificarlo personalmente una vez eliminada su cuenta.
          </p>
        </section>

        <section className="mb-8 leading-relaxed">
          <h2 className="font-display font-bold text-2xl border-l-4 border-[#C6E90C] pl-3 mb-3">Cómo solicitarla</h2>
          <p>
            Completa el formulario y haz clic en <b>Enviar solicitud</b>. Se abrirá tu app de correo con un mensaje
            pre-cargado hacia nuestro equipo. Procesaremos tu pedido en un plazo máximo de <b>30 días naturales</b> y
            te confirmaremos por correo cuando la eliminación esté completa.
          </p>

          <div className="mt-6 bg-neutral-50 border border-neutral-200 rounded-lg p-5 space-y-4">
            <div>
              <label className="block text-sm font-bold mb-1" htmlFor="email">Correo registrado en la app</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@ejemplo.com"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                data-testid="delete-account-email"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1" htmlFor="phone">Teléfono registrado</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+51 999 999 999"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                data-testid="delete-account-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1" htmlFor="reason">Motivo (opcional)</label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Nos ayuda a mejorar."
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                data-testid="delete-account-reason"
              />
            </div>
            <a
              href={mailtoHref()}
              className="inline-block bg-[#C6E90C] text-black font-bold px-6 py-3 rounded hover:bg-[#b6d80a] transition"
              data-testid="delete-account-submit"
            >
              Enviar solicitud →
            </a>
          </div>

          <p className="text-sm text-neutral-600 mt-4">
            También puedes escribirnos directamente a{" "}
            <a className="text-[#3949AB] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            {" "}con el asunto <i>"Eliminación de cuenta"</i>.
          </p>
        </section>

        <div className="border-t border-neutral-200 mt-12 pt-6 flex items-center justify-between text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} {COMPANY}. Todos los derechos reservados.</div>
          <div className="flex gap-4">
            <a href="/privacy-policy" className="text-[#3949AB] hover:underline">Política de Privacidad</a>
            <a href="/" className="text-[#3949AB] hover:underline">← Inicio</a>
          </div>
        </div>
      </div>
    </div>
  );
}
